export const FA_STATUS_LABELS = {
  draft: "Draft",
  in_progress: "In progress",
  completed_by_assessor: "Completed by assessor",
  awaiting_manager_review: "Awaiting manager review",
  returned_for_amendment: "Returned for amendment",
  approved: "Approved",
  further_assessment_required: "Further assessment required",
  superseded: "Superseded",
};

export const FA_STATUS_COLORS = {
  draft: "bg-amber-100 text-amber-800 border-transparent",
  in_progress: "bg-blue-50 text-blue-700 border-transparent",
  completed_by_assessor: "bg-blue-50 text-blue-700 border-transparent",
  awaiting_manager_review: "bg-purple-50 text-purple-700 border-transparent",
  returned_for_amendment: "bg-amber-100 text-amber-800 border-transparent",
  approved: "bg-ukgreen-light text-ukgreen border-transparent",
  further_assessment_required: "bg-red-50 text-red-700 border-transparent",
  superseded: "bg-slate-100 text-slate-500 border-transparent",
};

export const FINAL_RESPONSES = [
  { value: "independently_demonstrated", label: "Independently demonstrated", short: "IND" },
  { value: "minimal_prompt_required", label: "Minimal prompt required", short: "MIN" },
  { value: "prompt_required", label: "Prompt required", short: "PROMPT" },
  { value: "significant_support_required", label: "Significant support required", short: "SIG" },
  { value: "assessor_intervention_required", label: "Assessor intervention required", short: "INT" },
  { value: "not_demonstrated", label: "Not demonstrated", short: "ND" },
  { value: "not_assessed", label: "Not assessed", short: "NA" },
  { value: "not_applicable", label: "Not applicable", short: "—" },
];

export const COMMENT_REQUIRED_RESPONSES = ["significant_support_required", "assessor_intervention_required", "not_demonstrated"];

export const RESPONSE_LABELS = Object.fromEntries(FINAL_RESPONSES.map((r) => [r.value, r.label]));

export const FINAL_COMPETENCY_AREAS = [
  {
    key: "starting_journey", label: "Starting the Journey",
    items: [
      { key: "leaves_starting_point", label: "Leaves starting point appropriately" },
      { key: "has_travel_items", label: "Has required travel items" },
      { key: "identifies_destination", label: "Identifies intended destination" },
      { key: "begins_correct_route", label: "Begins correct route" },
    ],
  },
  {
    key: "walking", label: "Walking",
    items: [
      { key: "follows_walking_route", label: "Follows agreed walking route" },
      { key: "recognises_landmarks", label: "Recognises landmarks" },
      { key: "maintains_awareness", label: "Maintains appropriate awareness" },
      { key: "remains_on_route", label: "Remains on planned route" },
    ],
  },
  {
    key: "road_safety", label: "Road Safety",
    items: [
      { key: "stops_appropriately", label: "Stops appropriately before crossing" },
      { key: "looks_listens", label: "Looks / listens appropriately" },
      { key: "identifies_crossing", label: "Identifies appropriate crossing" },
      { key: "uses_crossing_correctly", label: "Uses crossing correctly" },
      { key: "remains_attentive", label: "Remains attentive near roads" },
      { key: "responds_to_traffic", label: "Responds safely to changing traffic conditions" },
      { key: "understands_traffic_movement", label: "Understands traffic movement" },
      { key: "manages_side_roads", label: "Manages side roads" },
      { key: "aware_of_parked_vehicles", label: "Aware of parked vehicles" },
      { key: "responds_to_unexpected_traffic", label: "Responds to unexpected traffic situations" },
    ],
  },
  {
    key: "transport", label: "Public Transport",
    items: [
      { key: "identifies_stop_station", label: "Identifies correct stop / station" },
      { key: "identifies_service", label: "Identifies correct service" },
      { key: "waits_appropriately", label: "Waits appropriately" },
      { key: "boards_safely", label: "Boards safely" },
      { key: "manages_travel_card", label: "Manages travel card / ticket" },
      { key: "identifies_direction", label: "Identifies correct direction" },
      { key: "monitors_journey", label: "Monitors journey" },
      { key: "identifies_destination_stop", label: "Identifies destination" },
      { key: "exits_correctly", label: "Exits correctly" },
      { key: "responds_to_transport_changes", label: "Responds to transport changes" },
    ],
  },
  {
    key: "interchange", label: "Interchange (where applicable)",
    items: [
      { key: "identifies_interchange", label: "Identifies interchange" },
      { key: "follows_correct_route", label: "Follows correct route" },
      { key: "locates_next_service", label: "Locates next service" },
      { key: "manages_waiting_safely", label: "Manages waiting safely" },
    ],
  },
  {
    key: "personal_safety", label: "Personal Safety & Stranger Awareness",
    items: [
      { key: "stranger_awareness", label: "Demonstrates appropriate stranger awareness" },
      { key: "personal_boundaries", label: "Maintains personal boundaries" },
      { key: "protects_personal_info", label: "Protects personal information" },
      { key: "identifies_help_sources", label: "Identifies appropriate sources of help" },
      { key: "identifies_transport_staff", label: "Identifies transport staff" },
      { key: "identifies_safe_adults", label: "Identifies safe adults / services" },
      { key: "responds_to_inappropriate_approach", label: "Responds appropriately to inappropriate approach" },
      { key: "action_if_feeling_unsafe", label: "Follows agreed action if feeling unsafe" },
    ],
  },
  {
    key: "communication", label: "Communication",
    items: [
      { key: "communicates_appropriately", label: "Communicates appropriately" },
      { key: "asks_for_assistance", label: "Asks for assistance when required" },
      { key: "communicates_problems", label: "Communicates problems" },
      { key: "uses_agreed_strategies", label: "Uses agreed communication strategies" },
    ],
  },
  {
    key: "problem_solving", label: "Problem Solving",
    items: [
      { key: "recognises_errors", label: "Recognises errors / problems" },
      { key: "attempts_solution", label: "Attempts appropriate solution" },
      { key: "follows_emergency_strategy", label: "Follows agreed emergency strategy" },
      { key: "seeks_assistance", label: "Seeks appropriate assistance" },
      { key: "identifies_incorrect_route", label: "Identifies incorrect route" },
      { key: "recovers_from_route_mistake", label: "Recovers appropriately from a route mistake" },
    ],
  },
  {
    key: "arrival", label: "Arrival",
    items: [
      { key: "recognises_destination", label: "Recognises destination" },
      { key: "exits_transport_correctly", label: "Exits transport correctly" },
      { key: "follows_final_walking_route", label: "Follows final walking route" },
      { key: "reaches_safe_destination", label: "Reaches agreed safe destination" },
    ],
  },
];

export const EMERGENCY_SCENARIOS = [
  { key: "service_does_not_arrive", label: "Bus/train does not arrive" },
  { key: "service_cancelled", label: "Service is cancelled" },
  { key: "route_diverted", label: "Route is diverted" },
  { key: "wrong_transport_taken", label: "Wrong transport is taken" },
  { key: "stop_missed", label: "Stop is missed" },
  { key: "becomes_lost", label: "Child becomes lost" },
  { key: "travel_card_fails", label: "Travel card fails" },
  { key: "travel_card_lost", label: "Travel card is lost" },
  { key: "phone_unavailable", label: "Mobile phone is unavailable" },
  { key: "becomes_unwell", label: "Child becomes unwell" },
  { key: "feels_unsafe", label: "Child feels unsafe" },
  { key: "approached_inappropriately", label: "Child is approached inappropriately" },
  { key: "transport_terminates_early", label: "Transport terminates unexpectedly" },
];

export const ASSESSMENT_METHODS = [
  { value: "observation", label: "Observation" },
  { value: "discussion", label: "Discussion" },
  { value: "scenario_testing", label: "Scenario testing" },
];

export const ASSESSOR_RECOMMENDATIONS = [
  { value: "successfully_completed", label: "Successfully completed" },
  { value: "completed_one_direction_only", label: "Successfully completed for one journey direction only" },
  { value: "further_training_required", label: "Further training required" },
  { value: "further_assessment_required", label: "Further assessment required" },
  { value: "pause_review_required", label: "Programme pause / review required" },
  { value: "not_currently_ready", label: "Not currently ready for independent travel" },
  { value: "other_manager_review", label: "Other — manager review" },
];

export const MANAGER_FINAL_DECISIONS = [
  { value: "approve_completion", label: "Approve successful completion" },
  { value: "approve_direction_only", label: "Approve completion for specific journey direction" },
  { value: "return_for_amendment", label: "Return for amendment" },
  { value: "require_additional_training", label: "Require additional training" },
  { value: "require_reassessment", label: "Require reassessment" },
  { value: "pause_review_programme", label: "Pause / review programme" },
  { value: "other_outcome", label: "Other authorised outcome" },
];

export const OBJECTIVE_FINAL_STATUSES = [
  { value: "achieved", label: "Achieved" },
  { value: "partially_achieved", label: "Partially achieved" },
  { value: "not_achieved", label: "Not achieved" },
  { value: "no_longer_applicable", label: "No longer applicable" },
];

export const SKILL_LABELS = {
  road_safety: "Road Safety", route_recognition: "Route Recognition", public_transport: "Public Transport",
  travel_card: "Travel Card", stranger_awareness: "Stranger Awareness", communication: "Communication",
  emergency_response: "Emergency Response", problem_solving: "Problem Solving", overall_journey: "Overall Journey",
};

export const DIRECTION_LABELS = { outbound: "Home → School", return: "School → Home" };
